import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Box, Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem, Button, Stack, IconButton, Divider, Autocomplete, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { jerseyApi, Colour, ElementType, Pattern } from '../../api/jerseyApi';

export type InteractiveElement = {
  element_type: string;
  color: string | null;
  patterns: string[];
  pattern_colors: Record<string, string[]>;
  order: number;
  // Preserve additional parser metadata when available
  explicit_color?: boolean;
  visibility_adjusted?: boolean;
  raw_text?: string;
};

export type InteractiveData = {
  base_color: string | null;
  elements: InteractiveElement[];
};

interface InteractiveEditorProps {
  value?: InteractiveData;
  onChange: (value: InteractiveData) => void;
}

const DEFAULT_ELEMENTS_ORDER = ['base'];

const normalizeOrders = (els: InteractiveElement[]) => els.map((e, idx) => ({ ...e, order: idx }));

const InteractiveEditor: React.FC<InteractiveEditorProps> = ({ value, onChange }) => {
  const [colours, setColours] = useState<Colour[]>([]);
  const [displayedColours, setDisplayedColours] = useState<Colour[]>([]); // Separate state for displayed colors
  const [coloursLoading, setColoursLoading] = useState<boolean>(true);
  const [elementTypes, setElementTypes] = useState<ElementType[]>([]);
  const [patternsByElement, setPatternsByElement] = useState<Record<number, Pattern[]>>({});
  const [data, setData] = useState<InteractiveData>(() => value || {
    base_color: null,
    elements: normalizeOrders(DEFAULT_ELEMENTS_ORDER.map((name, idx) => ({
      element_type: name,
      color: name === 'base' ? null : null,
      patterns: [],
      pattern_colors: {},
      order: idx,
    })) as InteractiveElement[]),
  });
  const [isHydrated, setIsHydrated] = useState<boolean>(!!value);
  const [colorSearchLoading, setColorSearchLoading] = useState<boolean>(false);
  const colorSearchTimerRef = useRef<any>(null);



  // Keep internal state in sync when external value changes (e.g., switching from Text -> Interactive)
  // IMPORTANT: Only hydrate AFTER colors are loaded to avoid race condition
  useEffect(() => {
    if (!value) return;
    if (coloursLoading) {
      return; // Wait for colors to load first
    }

    try {
      const prev = JSON.stringify(data);
      const next = JSON.stringify(value);
      if (prev !== next) {
        // Now set the data - colors should be available
        const normalized: InteractiveData = {
          base_color: value.base_color || null,
          elements: normalizeOrders((value.elements || []) as InteractiveElement[]),
        };
        setData(normalized);
        setIsHydrated(true);
      }
    } catch {
      const normalized: InteractiveData = {
        base_color: value.base_color || null,
        elements: normalizeOrders((value.elements || []) as InteractiveElement[]),
      };
      setData(normalized);
      setIsHydrated(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, coloursLoading, colours.length]); // Re-run when colors finish loading



  // Load colours and element types
  useEffect(() => {
    const load = async () => {
      try {
        setColoursLoading(true);
        const [colPage, ets] = await Promise.all([
          jerseyApi.listColors({ skip: 0, limit: 1000 }), // Load more colors initially to ensure we have the common ones
          jerseyApi.getElementTypes(),
        ]);
        setColours(colPage.items);
        setDisplayedColours(colPage.items); // Initialize displayed colors
        setElementTypes(ets);
      } catch (e) {
        console.error('Failed loading reference data', e);
      } finally {
        setColoursLoading(false);
      }
    };
    load();
  }, []);

  // Server-side color search with debouncing (like color tab)
  const searchColors = useCallback(async (searchTerm: string) => {
    setColorSearchLoading(true);
    try {
      const response = await jerseyApi.listColors({
        skip: 0,
        limit: 100,
        search: searchTerm || undefined,
      });
      setDisplayedColours(response.items); // Update displayed colors only, not the base colours
    } catch (e) {
      console.error('Failed searching colors', e);
    } finally {
      setColorSearchLoading(false);
    }
  }, []);

  const handleColorSearch = useCallback((searchTerm: string) => {
    // Clear existing timer
    if (colorSearchTimerRef.current) {
      clearTimeout(colorSearchTimerRef.current);
    }

    // Debounce search by 300ms
    colorSearchTimerRef.current = setTimeout(() => {
      searchColors(searchTerm);
    }, 300);
  }, [searchColors]);

  // Debounced server-side pattern fetch for an element type id (supports search)
  const patternSearchRef = useRef<Record<number, { timer?: any; last?: string }>>({});
  const ensurePatternsForElementType = async (elementTypeId?: number, search?: string, immediate?: boolean) => {
    if (!elementTypeId) return;
    // If no search provided and we already have cached options, avoid refetch
    if (!search && patternsByElement[elementTypeId]) return;

    const doFetch = async () => {
      try {
        const res = await jerseyApi.getPatterns({
          element_type_id: elementTypeId,
          size: 250,
          lightweight: true,
          search: search || undefined,
        });
        setPatternsByElement(prev => ({ ...prev, [elementTypeId]: res.items }));
        if (!patternSearchRef.current[elementTypeId]) patternSearchRef.current[elementTypeId] = {} as any;
        patternSearchRef.current[elementTypeId].last = search || '';
      } catch (e) {
        console.error('Failed loading patterns', e);
      }
    };

    if (immediate) return doFetch();

    // Debounce per element type to avoid flooding the API while typing
    if (!patternSearchRef.current[elementTypeId]) patternSearchRef.current[elementTypeId] = {} as any;
    const prev = patternSearchRef.current[elementTypeId];
    if (prev.timer) clearTimeout(prev.timer);
    prev.timer = setTimeout(doFetch, 250);
  };

  // Map element_type name (with synonyms) to id
  const elementTypeIdByName = useMemo(() => {
    const map: Record<string, number> = {};
    elementTypes.forEach(et => {
      const n = et.name.toLowerCase();
      map[n] = et.id;
      // synonyms only
      if (n === 'body') map['shirt'] = et.id; // treat body as shirt
      if (n === 'shirt') map['body'] = et.id;
      if (n === 'sleeves') map['sleeve'] = et.id;
      if (n === 'sleeve') map['sleeves'] = et.id;
    });
    return map;
  }, [elementTypes]);

  // Update parent on changes (avoid firing before hydration from parent value)
  useEffect(() => {
    if (value && !isHydrated) return;
    const baseEl = data.elements.find(e => e.element_type === 'base');
    const baseColor = baseEl?.color || data.base_color || null;
    onChange({ base_color: baseColor, elements: data.elements });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isHydrated]);

  // Normalize element types for filtering and UI
  const resolveElementTypeKey = useCallback((name: string) => {
    const n = name.toLowerCase();
    if (n === 'sleeve') return 'sleeves';
    if (n === 'body') return 'shirt';
    return n;
  }, []);

  const toDisplayElementType = useCallback((name: string) => {
    const n = name.toLowerCase();
    if (n === 'sleeve') return 'sleeves';
    if (n === 'body') return 'shirt';
    return n;
  }, []);

  const toStoredElementType = useCallback((displayName: string) => {
    const n = displayName.toLowerCase();
    if (n === 'sleeve') return 'sleeves';
    if (n === 'body') return 'shirt';
    return n;
  }, []);

  const availableElementTypeNames = useMemo(() => {
    // Build display names from backend element types
    const normalized = new Set<string>();
    elementTypes.forEach(et => normalized.add(toDisplayElementType(et.name)));
    return Array.from(normalized);
  }, [elementTypes, toDisplayElementType]);

  // Preload patterns for current elements once element types are known
  useEffect(() => {
    if (elementTypes.length === 0) return;
    (async () => {
      for (const el of data.elements) {
        const stored = el.element_type;
        const key = resolveElementTypeKey(stored);
        const etId = elementTypeIdByName[key];
        if (etId) await ensurePatternsForElementType(etId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementTypes.length]);

  const handleAddElement = () => {
    // Add the first missing known element type (excluding base)
    const existing = new Set(data.elements.map(e => resolveElementTypeKey(e.element_type)));
    const next = availableElementTypeNames.find(n => !existing.has(resolveElementTypeKey(n)) && n !== 'base');
    if (!next) return;
    const newEl: InteractiveElement = { element_type: next, color: null, patterns: [], pattern_colors: {}, order: data.elements.length };
    setData(d => ({ ...d, elements: normalizeOrders([...(d.elements as InteractiveElement[]), newEl]) }));
  };

  const handleRemoveElement = (order: number) => {
    setData(d => ({
      ...d,
      elements: d.elements.filter(e => e.order !== order).map((e, idx) => ({ ...e, order: idx })),
    }));
  };

  const setElement = (order: number, updater: (el: InteractiveElement) => InteractiveElement) => {
    setData(d => ({
      ...d,

      elements: normalizeOrders(d.elements.map(e => (e.order === order ? updater(e) : e)) as InteractiveElement[]),
    }));
  };

  // colorNames removed; use full colour objects for swatches

  const renderElementRow = (el: InteractiveElement) => {
    const key = resolveElementTypeKey(el.element_type);
    const etId = elementTypeIdByName[key];
    const patterns = etId ? (patternsByElement[etId] || []) : [];
    const patternOptions = patterns.map(p => p.description || p.slug);

    // Guard Select values to avoid MUI out-of-range warnings when options aren't loaded yet
    const displayElement = toDisplayElementType(el.element_type);
    const displayValue = availableElementTypeNames.includes(displayElement) ? displayElement : '';

  const moveElement = (order: number, delta: number) => {
    setData(d => {
      const arr = [...(d.elements as InteractiveElement[])];
      const idx = order;
      const target = Math.max(0, Math.min(arr.length - 1, idx + delta));
      if (target === idx) return d;
      const copy = [...arr];
      const [moved] = copy.splice(idx, 1);
      copy.splice(target, 0, moved);
      return { ...d, elements: normalizeOrders(copy) };
    });
  };


    const colorsLoaded = colours.length > 0;

    return (
      <Paper key={`${el.element_type}-${el.order}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Element</InputLabel>
              <Select
                displayEmpty
                renderValue={(val) => (val ? String(val) : 'Select element')}
                label="Element"
                value={displayValue}
                disabled={availableElementTypeNames.length === 0}
                onChange={async (e) => {
                  const nextDisplay = String(e.target.value);
                  const stored = toStoredElementType(nextDisplay);
                  const nextKey = resolveElementTypeKey(stored);
                  const nextId = elementTypeIdByName[nextKey];
                  await ensurePatternsForElementType(nextId);
                  setElement(el.order, (prev) => ({ ...prev, element_type: stored, patterns: [], pattern_colors: {} }));
                }}
              >
                <MenuItem value=""><em>none</em></MenuItem>
                {availableElementTypeNames.map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Autocomplete
              size="small"
              options={(() => {
                // Ensure selected color is always in options to prevent value from disappearing
                const selectedColor = colours.find(c => c.name?.toLowerCase() === el.color?.toLowerCase());
                if (selectedColor && !displayedColours.find(c => c.name?.toLowerCase() === selectedColor.name?.toLowerCase())) {
                  return [selectedColor, ...displayedColours];
                }
                return displayedColours;
              })()}
              getOptionLabel={(o) => o?.name ?? ''}
              value={colours.find(c => c.name?.toLowerCase() === el.color?.toLowerCase()) || null}
              onChange={(_, val) => setElement(el.order, (prev) => ({ ...prev, color: val ? val.name : null }))}
              onInputChange={(_, value) => handleColorSearch(value)}
              loading={colorSearchLoading || coloursLoading}
              ListboxProps={{
                style: { maxHeight: '400px' }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.name} style={{ padding: '8px 16px' }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: option.hexadecimal, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(o, v) => o.name?.toLowerCase() === v.name?.toLowerCase()}
              disableClearable={false}
              disabled={!colorsLoaded || coloursLoading}
              renderInput={(params) => (
                <TextField {...params} label="Color" placeholder="Search colours" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              size="small"
              options={["None", ...patternOptions]}
              value={el.patterns[0] ? el.patterns[0] : "None"}
              onChange={(_, val) => {
                const pat = typeof val === 'string' ? val : null;
                setElement(el.order, (prev) => ({
                  ...prev,
                  patterns: !pat || pat === 'None' ? [] : [pat],
                  pattern_colors: !pat || pat === 'None' ? {} : { [pat]: (prev.pattern_colors[pat] || []) },
                }));
              }}
              ListboxProps={{
                style: { maxHeight: '400px' }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option} style={{ padding: '8px 16px' }}>
                  <Typography variant="body2" sx={{ width: '100%' }}>{option}</Typography>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pattern"
                  placeholder={!etId ? 'Select an element type' : 'Search patterns'}
                />
              )}
              disabled={!etId}
              loading={!!etId && !patternsByElement[etId]}
              onOpen={() => ensurePatternsForElementType(etId, undefined, true)}
              onInputChange={(_, val) => ensurePatternsForElementType(etId, val || undefined)}
              isOptionEqualToValue={(o, v) => o === v}
            />
          </Grid>

          <Grid item xs={12} sm={2} sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <IconButton aria-label="move up" size="small" onClick={() => moveElement(el.order, -1)} disabled={el.order === 0}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton aria-label="move down" size="small" onClick={() => moveElement(el.order, 1)} disabled={el.order === (data.elements.length - 1)}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            {el.element_type !== 'base' && (
              <IconButton aria-label="remove" onClick={() => handleRemoveElement(el.order)}>
                <DeleteIcon />
              </IconButton>
            )}
          </Grid>

          {/* Pattern colours */}
          {el.patterns.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Pattern colours {(el.pattern_colors[el.patterns[0]] || []).length > 0 && `(${(el.pattern_colors[el.patterns[0]] || []).length})`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Colors are applied in order: 1st color → pattern-1, 2nd → pattern-2, etc. Use arrows to reorder.
              </Typography>
              <Stack spacing={0.5} sx={{ mb: 1 }}>
                {(el.pattern_colors[el.patterns[0]] || []).map((c, idx) => {
                  const colorObj = colours.find(col => col.name === c);
                  return (
                    <Paper key={`${c}-${idx}`} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ minWidth: 24, fontWeight: 'bold', color: 'text.secondary' }}>
                        #{idx + 1}
                      </Typography>
                      {colorObj && (
                        <Box sx={{ width: 24, height: 24, borderRadius: '4px', bgcolor: colorObj.hexadecimal, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
                      )}
                      <Typography variant="body2" sx={{ flex: 1 }}>{c}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const pat = el.patterns[0];
                          setElement(el.order, (prev) => {
                            const list = [...(prev.pattern_colors[pat] || [])];
                            if (idx > 0) {
                              [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
                            }
                            return { ...prev, pattern_colors: { ...prev.pattern_colors, [pat]: list } };
                          });
                        }}
                        disabled={idx === 0}
                        aria-label="move up"
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const pat = el.patterns[0];
                          setElement(el.order, (prev) => {
                            const list = [...(prev.pattern_colors[pat] || [])];
                            const maxIdx = list.length - 1;
                            if (idx < maxIdx) {
                              [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
                            }
                            return { ...prev, pattern_colors: { ...prev.pattern_colors, [pat]: list } };
                          });
                        }}
                        disabled={idx === (el.pattern_colors[el.patterns[0]] || []).length - 1}
                        aria-label="move down"
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const pat = el.patterns[0];
                          setElement(el.order, (prev) => {
                            const list = (prev.pattern_colors[pat] || []).filter((_, i) => i !== idx);
                            return { ...prev, pattern_colors: { ...prev.pattern_colors, [pat]: list } };
                          });
                        }}
                        aria-label="remove"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  );
                })}
              </Stack>
              <Box>
                <Autocomplete
                  size="small"
                  options={displayedColours}
                  getOptionLabel={(o) => o?.name ?? ''}
                  value={null}
                  onChange={(_, val) => {
                    if (!val) return;
                    const pat = el.patterns[0];
                    setElement(el.order, (prev) => {
                      const list = [...(prev.pattern_colors[pat] || []), val.name];
                      return { ...prev, pattern_colors: { ...prev.pattern_colors, [pat]: list } };
                    });
                  }}
                  onInputChange={(_, value) => handleColorSearch(value)}
                  loading={colorSearchLoading}
                  ListboxProps={{
                    style: { maxHeight: '400px' }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.name} style={{ padding: '8px 16px' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: option.hexadecimal, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  isOptionEqualToValue={(o, v) => o.name?.toLowerCase() === v.name?.toLowerCase()}
                  renderInput={(params) => (
                    <TextField {...params} label="Add colour" placeholder="Search colours" />
                  )}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  // Base colour convenience (can be changed via base element colour dropdown)
  const baseElement = useMemo(() => data.elements.find(e => e.element_type === 'base'), [data.elements]);

  // Ensure base element exists only if there are no elements at all; do not remove parsed elements
  useEffect(() => {
    if (data.elements.length === 0) {
      setData(d => ({
        ...d,
        elements: [{ element_type: 'base', color: d.base_color || null, patterns: [], pattern_colors: {}, order: 0 }],
      }));
    }
  }, [data.elements.length]);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Interactive Editor</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select an element, choose a base colour, then optionally add a pattern and pattern colours. The preview updates live.
      </Typography>

      {/* Loading indicator */}
      {coloursLoading && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading colors...
          </Typography>
        </Paper>
      )}

      {/* Don't render editor until colors are loaded */}
      {coloursLoading ? null : (
        <>
      {/* Base colour quick selector */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="body2"><strong>Shirt (base)</strong></Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Autocomplete
              size="small"
              options={(() => {
                // Ensure selected color is always in options to prevent value from disappearing
                const selectedColor = colours.find(c => c.name?.toLowerCase() === (baseElement?.color || '')?.toLowerCase());
                if (selectedColor && !displayedColours.find(c => c.name?.toLowerCase() === selectedColor.name?.toLowerCase())) {
                  return [selectedColor, ...displayedColours];
                }
                return displayedColours;
              })()}
              getOptionLabel={(o) => o?.name ?? ''}
              value={colours.find(c => c.name?.toLowerCase() === (baseElement?.color || '')?.toLowerCase()) || null}
              onChange={(_, val) => setElement(baseElement?.order ?? 0, (prev) => ({ ...prev, color: val ? val.name : null }))}
              onInputChange={(_, value) => handleColorSearch(value)}
              loading={colorSearchLoading || coloursLoading}
              disabled={coloursLoading}
              ListboxProps={{
                style: { maxHeight: '400px' }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.name} style={{ padding: '8px 16px' }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: option.hexadecimal, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(o, v) => o.name?.toLowerCase() === v.name?.toLowerCase()}
              renderInput={(params) => (
                <TextField {...params} label="Base colour" placeholder="Search colours" />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 2 }} />

      {/* Elements editor */}
      {data.elements.map(renderElementRow)}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddElement}>
          Add element
        </Button>
      </Box>
      </>
      )}
    </Box>
  );
};

export default InteractiveEditor;

